import React from 'react';

import { range } from 'lodash';
import { Dayjs } from 'dayjs';
import { css, styled } from '@mui/material/styles';

import { dayjsWithTimezone } from '@/src/utils';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { useTranslation } from '@/src/i18n';

type AvailabilityTimeProps = {
  isSubstitute: boolean;
  isSelected: boolean;
};

const AvailabilityTime = styled('button', {
  shouldForwardProp: propName =>
    propName !== 'isSelected' && propName !== 'isSubstitute',
})<AvailabilityTimeProps>(
  ({ theme, isSubstitute, isSelected }) => css`
    color: ${theme.palette.text.primary};
    border-width: 0;
    width: 46px; // custom value to match old design 
    margin: 5px ${theme.spacing(0.5)};
    padding: ${theme.spacing(1)} ${theme.spacing(0.5)};
    font-size: ${theme.typography.body2.fontSize};
    font-weight: ${theme.typography.button.fontWeight};
    border-radius: ${theme.spacing(0.5)};
    background-color: ${theme.palette.secondary.light};
    text-align: center;
    cursor: pointer;

    &:hover {
      background-color: ${theme.palette.secondary.main};
      color: ${theme.palette.common.white};
    }

    // Substitute
    ${isSubstitute &&
      css`
        position: relative;

        &:after {
          content: '';
          position: absolute;
          top: 2px;
          right: 2px;
          height: 8px;
          width: 8px;
          border-radius: 100%;
          background: ${theme.palette.secondary.main};
        }

        &:hover {
          &:after {
            background-color: ${theme.palette.secondary.light};
          }
        }
      `}

    // In case on taking multiple appointments at once
    ${isSelected &&
      css`
        background-color: ${theme.palette.secondary.main};
        color: ${theme.palette.common.white};
      `}
  `,
);

type Props = {
  date: Dayjs;
  slots: any[];
  viewableSlots: number;
  onClick: Function;
  childPrefixKey: string;
  timezone: string;
  selectedItems: any[];
};

const AvailabilityDay = ({
  date,
  slots,
  viewableSlots,
  onClick,
  childPrefixKey,
  timezone,
  selectedItems,
}: Props) => {
  const { t } = useTranslation();
  const dayjsTZ = dayjsWithTimezone(timezone);
  const emptySlotsCount = Math.max(0, viewableSlots - slots.length);

  return (
    <div className="availability-day">
      <div className="availability-day__header pnl_availability_day">
        <span className="day">{`${dayjsTZ(date).format('ddd')}`}</span>
        <span className="date">{`${dayjsTZ(date).format('DD MMM')}`}</span>
      </div>
      <div className="availability-day__slots">
        {slots.slice(0, viewableSlots).map((slot, i) => (
          <AvailabilityTime
            key={`${childPrefixKey}_${slot.id}`}
            isSubstitute={Boolean(slot?.substitute?.userId)}
            isSelected={Boolean(selectedItems.some(s => s?.id === slot?.id))}
            onClick={() => {
              analyticsEvent({
                category: 'Booking',
                action: 'ClickAvailability',
              });
              onClick(slot);
            }}
            data-cy={`btn-calendar-select-slot-${i}`}
            type="button" // by default it submits the form !
          >
            {dayjsTZ(slot.startDateTime).format('HH:mm')}
          </AvailabilityTime>
        ))}
        {range(emptySlotsCount).map(i => (
          <div
            key={`${childPrefixKey}_${i}`}
            className="availability-slot--empty"
          >
            {t('empty_slot_dash')}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvailabilityDay;
